import {
  ChevronDown,
  ClipboardList,
  Clock,
  Gift,
  LayoutDashboard,
  Settings,
  ShoppingBag,
  Star,
  Store,
  Truck,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useNavigate } from 'react-router-dom';

import type { MeRestaurantMembership } from '../../lib/manageEndpoints';
import { LanguagePillSwitcher } from '../LanguagePillSwitcher';
import { Logo } from '../Logo';

interface ManageSidebarProps {
  restaurants: MeRestaurantMembership[];
  currentRestaurantId?: string;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  APPROVED: 'bg-palm/10 text-palm',
  PENDING_REVIEW: 'bg-amber-100 text-amber-700',
  DRAFT: 'bg-muted text-muted-foreground',
  PAUSED: 'bg-amber-100 text-amber-700',
  REJECTED: 'bg-destructive/10 text-destructive',
  SUSPENDED: 'bg-destructive/10 text-destructive',
};

const navLinkClassName = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
    isActive
      ? 'bg-sidebar-foreground/10 font-medium text-sidebar-foreground'
      : 'text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground'
  }`;

function SidebarContent({
  restaurants,
  currentRestaurantId,
  onNavigate,
}: {
  restaurants: MeRestaurantMembership[];
  currentRestaurantId?: string;
  onNavigate: () => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation('manage');
  const currentRestaurant = currentRestaurantId
    ? restaurants.find((r) => r.restaurantId === currentRestaurantId)
    : undefined;
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const basePath = `/manage/${currentRestaurantId}`;

  const navItems = [
    { to: '', label: t('nav.dashboard'), icon: LayoutDashboard, end: true },
    { to: 'orders', label: t('nav.orders'), icon: ShoppingBag },
    { to: 'menu', label: t('nav.menu'), icon: ClipboardList },
    { to: 'reviews', label: t('nav.reviews'), icon: Star },
    { to: 'hours', label: t('nav.openingHours'), icon: Clock },
    { to: 'delivery', label: t('nav.delivery'), icon: Truck },
    { to: 'vouchers', label: t('nav.vouchers'), icon: Gift },
    { to: 'payouts', label: t('nav.payouts'), icon: Wallet },
    { to: 'team', label: t('nav.team'), icon: Users },
    { to: 'settings', label: t('nav.settings'), icon: Settings },
  ];

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex h-16 shrink-0 items-center border-b border-sidebar-foreground/10 px-4">
        <Link className="flex items-center gap-2" onClick={onNavigate} to="/">
          <Logo className="h-8 w-auto invert" variant="black" />
        </Link>
        <span className="ml-2 rounded-md bg-sidebar-foreground/10 px-1.5 py-0.5 text-xs font-medium text-sidebar-foreground/70">
          {t('nav.manage')}
        </span>
      </div>

      {currentRestaurant && (
        <div className="shrink-0 border-b border-sidebar-foreground/10 p-3">
          <button
            className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-sidebar-foreground/10"
            onClick={() => setSwitcherOpen((v) => !v)}
            type="button"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-sidebar-foreground">
                {currentRestaurant.name}
              </p>
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASSES[currentRestaurant.status] ?? ''}`}
              >
                {t(`statusLabels.${currentRestaurant.status}`, currentRestaurant.status)}
              </span>
            </div>
            {restaurants.length > 1 && (
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-sidebar-foreground/50 transition-transform ${switcherOpen ? 'rotate-180' : ''}`}
              />
            )}
          </button>

          {switcherOpen && restaurants.length > 1 && (
            <div className="mt-1 rounded-lg border border-sidebar-foreground/10 bg-sidebar py-1 shadow-lg">
              {restaurants
                .filter((r) => r.restaurantId !== currentRestaurantId)
                .map((r) => (
                  <button
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground"
                    key={r.restaurantId}
                    onClick={() => {
                      setSwitcherOpen(false);
                      onNavigate();
                      navigate(`/manage/${r.restaurantId}`);
                    }}
                    type="button"
                  >
                    <span className="truncate font-medium">{r.name}</span>
                    <span
                      className={`ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-xs ${STATUS_BADGE_CLASSES[r.status] ?? ''}`}
                    >
                      {t(`statusLabels.${r.status}`, r.status)}
                    </span>
                  </button>
                ))}
              <div className="mt-1 border-t border-sidebar-foreground/10 pt-1">
                <Link
                  className="flex w-full items-center px-3 py-2 text-sm text-accent hover:bg-sidebar-foreground/10"
                  onClick={() => {
                    setSwitcherOpen(false);
                    onNavigate();
                  }}
                  to="/manage/onboarding"
                >
                  {t('nav.addRestaurant')}
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      <nav className="flex-1 overflow-y-auto p-3">
        <ul className="space-y-0.5">
          <li>
            <NavLink className={navLinkClassName} end onClick={onNavigate} to="/manage">
              <Store className="h-4 w-4 shrink-0" />
              {t('nav.myRestaurants')}
            </NavLink>
          </li>
          {currentRestaurantId
            ? navItems.map((item) => {
                const Icon = item.icon;
                const to = item.to ? `${basePath}/${item.to}` : basePath;
                return (
                  <li key={item.to}>
                    <NavLink
                      className={navLinkClassName}
                      end={item.end}
                      onClick={onNavigate}
                      to={to}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {item.label}
                    </NavLink>
                  </li>
                );
              })
            : null}
        </ul>
        {!currentRestaurantId && (
          <p className="mt-3 px-3 text-xs text-sidebar-foreground/50">{t('home.selectToManage')}</p>
        )}
      </nav>

      <div className="shrink-0 border-t border-sidebar-foreground/10 p-3 space-y-1">
        <Link
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground"
          onClick={onNavigate}
          to="/"
        >
          {t('nav.customerSite')}
        </Link>
        <LanguagePillSwitcher className="mt-1" />
      </div>
    </div>
  );
}

export function ManageSidebar({
  restaurants,
  currentRestaurantId,
  mobileOpen,
  onMobileClose,
}: ManageSidebarProps) {
  const { t } = useTranslation('manage');
  return (
    <>
      <aside className="hidden h-full w-64 shrink-0 flex-col border-r border-sidebar-foreground/10 bg-sidebar md:flex">
        <SidebarContent
          currentRestaurantId={currentRestaurantId}
          onNavigate={() => {}}
          restaurants={restaurants}
        />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
            onClick={onMobileClose}
          />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-sidebar shadow-xl">
            <button
              aria-label={t('nav.closeMenu')}
              className="absolute right-3 top-3 rounded-lg p-1.5 text-sidebar-foreground/70 hover:bg-sidebar-foreground/10"
              onClick={onMobileClose}
              type="button"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent
              currentRestaurantId={currentRestaurantId}
              onNavigate={onMobileClose}
              restaurants={restaurants}
            />
          </aside>
        </div>
      )}
    </>
  );
}
